import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Instagram, Facebook, Twitter, Youtube } from "lucide-react";
import "../styles/Footer.css";
import logo from '../assets/logo.svg';
import { supabase } from '../services/supabase';
import { siteContentApi } from '../api/phpApi';

const iconMap = {
  linkedin: Linkedin,
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube
};

const DEFAULT_FOOTER_DATA = {
  logo_alt: 'IAMAR Logo',
  description: "Plateforme d'excellence pour l'innovation, la gouvernance et le développement en Afrique. Nous connectons les idées et créons les événements majeurs d'impact continental.",
  quick_links: [
    { text: "Accueil", href: "/" },
    { text: "À Propos", href: "/about" },
    { text: "Congrès GSIDA 2026", href: "/gsida" },
    { text: "Sommet APSIS", href: "/apsis" },
    { text: "Compétition Doctorale", href: "/challenge/doctoral-competition" },
    { text: "Programmes Exécutifs", href: "/trainings/executive-programs" },
    { text: "Revue Scientifique RIGSIDA", href: "/revue" },
    { text: "Publications & Actes", href: "/publications" },
    { text: "Contact", href: "/contact" }
  ],
  contact_info: {
    title: "Contact",
    address: "Agadir, Maroc",
    phone: "+212 5 28 00 00 00",
    email: "contact@iamar.ma"
  },
  social_section: {
    title: "Suivez-nous",
    description: "Restez informé des temps forts de nos événements, appels à communications et rapports d'experts."
  },
  social_links: [
    { platform: 'linkedin', url: 'https://linkedin.com' },
    { platform: 'facebook', url: 'https://facebook.com' },
    { platform: 'instagram', url: 'https://instagram.com' },
    { platform: 'twitter', url: 'https://twitter.com' }
  ],
  copyright_text: `© ${new Date().getFullYear()} IAMAR. Tous droits réservés.`
};

export default function Footer() {
  const [data, setData] = useState(DEFAULT_FOOTER_DATA);

  useEffect(() => {
    fetchFooterData();
  }, []);

  const fetchFooterData = async () => {
    try {
      // 1. Check custom footer settings from PHP API if saved
      try {
        const customFooter = await siteContentApi.getByKey('footer_settings');
        if (customFooter && customFooter.content) {
          const parsed = JSON.parse(customFooter.content);
          setData(prev => ({
            ...prev,
            ...parsed,
            contact_info: { ...prev.contact_info, ...(parsed.contact_info || {}) },
            social_section: { ...prev.social_section, ...(parsed.social_section || {}) },
            quick_links: (parsed.quick_links && parsed.quick_links.length > 0) ? parsed.quick_links : prev.quick_links,
            social_links: (parsed.social_links && parsed.social_links.length > 0) ? parsed.social_links : prev.social_links
          }));
          return;
        }
      } catch (e) {
        // Continue to other sources silently
      }

      // 2. Synchronize contact and social networks with header settings if available
      try {
        const navSettings = await siteContentApi.getByKey('header_nav_settings');
        if (navSettings && navSettings.content) {
          const parsedNav = JSON.parse(navSettings.content);
          setData(prev => ({
            ...prev,
            contact_info: {
              ...prev.contact_info,
              phone: parsedNav.top_phone || prev.contact_info.phone,
              email: parsedNav.top_email || prev.contact_info.email
            },
            social_links: [
              { platform: 'linkedin', url: parsedNav.linkedin_url || 'https://linkedin.com' },
              { platform: 'facebook', url: parsedNav.facebook_url || 'https://facebook.com' },
              { platform: 'instagram', url: parsedNav.instagram_url || 'https://instagram.com' },
              { platform: 'twitter', url: parsedNav.twitter_url || 'https://twitter.com' }
            ]
          }));
        }
      } catch (e) {
        // Fallback silently
      }

      // 3. Fallback to Supabase if configured
      if (supabase && process.env.REACT_APP_SUPABASE_URL) {
        const { data: footerData } = await supabase
          .from('footer')
          .select('*')
          .single();

        if (footerData) {
          setData(prev => ({
            ...prev,
            logo_alt: footerData.logo_alt || prev.logo_alt,
            description: footerData.description || prev.description,
            quick_links: (footerData.quick_links && footerData.quick_links.length > 0) ? footerData.quick_links : prev.quick_links,
            contact_info: footerData.contact_info ? { ...prev.contact_info, ...footerData.contact_info } : prev.contact_info,
            social_section: footerData.social_section ? { ...prev.social_section, ...footerData.social_section } : prev.social_section,
            social_links: (footerData.social_links && footerData.social_links.length > 0) ? footerData.social_links : prev.social_links,
            copyright_text: footerData.copyright_text || prev.copyright_text
          }));
        }
      }
    } catch (error) {
      console.warn('Could not load dynamic footer content, using fallback:', error);
    }
  };

  return (
    <footer className="footer">
      <div className="footer-container">
        
        {/* About */}
        <div className="footer-column">
          <div className="footer-logo">
            <Link to="/">
              <img src={logo} alt={data.logo_alt || "IAMAR"} className="logo-img" />
            </Link>
          </div>
          <p>{data.description}</p>
        </div>

        {/* Quick Links */}
        {data.quick_links && data.quick_links.length > 0 && (
          <div className="footer-column">
            <h3>Liens rapides</h3>
            <ul>
              {data.quick_links.map((link, index) => {
                const isExternal = link.href.startsWith('http') || link.href.startsWith('mailto:') || link.href.startsWith('tel:');
                return (
                  <li key={index}>
                    {isExternal ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer">
                        {link.text}
                      </a>
                    ) : (
                      <Link to={link.href}>
                        {link.text}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Contact */}
        {data.contact_info && data.contact_info.title && (
          <div className="footer-column">
            <h3>{data.contact_info.title}</h3>
            <ul>
              {data.contact_info.address && (
                <li><MapPin size={16} className="icon" /> {data.contact_info.address}</li>
              )}
              {data.contact_info.phone && (
                <li>
                  <Phone size={16} className="icon" /> 
                  <a href={`tel:${data.contact_info.phone}`}>{data.contact_info.phone}</a>
                </li>
              )}
              {data.contact_info.email && (
                <li>
                  <Mail size={16} className="icon" /> 
                  <a href={`mailto:${data.contact_info.email}`}>{data.contact_info.email}</a>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Follow us */}
        {data.social_section && data.social_section.title && (
          <div className="footer-column">
            <h3>{data.social_section.title}</h3>
            {data.social_section.description && <p>{data.social_section.description}</p>}
            {data.social_links && data.social_links.length > 0 && (
              <div className="social-icons">
                {data.social_links.map((link, index) => {
                  const IconComponent = iconMap[link.platform];
                  return IconComponent ? (
                    <a 
                      key={index}
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className={link.platform}
                      aria-label={link.platform}
                    >
                      <IconComponent size={18} />
                    </a>
                  ) : null;
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="footer-bottom">
        {data.copyright_text}
      </div>
    </footer>
  );
}
